// Modules
import React, { useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
// Components
import Layout from '~/components/layout';
import ErrorPage from '~/components/error-page';
import CategoryPageContent from '~/components/category-page';
import SubcategoryPageContent from '~/components/subcategory-page';
// Utils
import { isEmpty } from '~/utils';
import api from '~/services/api';
import { shouldIndex } from '~/utils/robots-constants';

const CategoryPage = ({
  categoryResponse,
  categoryStatus,
  subCategoryResponse = [],
  categoryPostsResponse,
  shouldIndexPage
}) => {
  const router = useRouter();

  const {
    query: { slug },
  } = router;

  const {
    title,
    meta_title,
    meta_description,
    cover_image,
    robots,
    childs: children = [],
    parent,
  } = categoryResponse;

  useEffect(() => {
    document.body.scrollTop = 0;
  }, [slug]);

  const breadCrumbsScheme = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: isEmpty(parent)
      ? [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'خانه',
            item: `${process.env.SITE_URL}`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: title,
            item: `${process.env.SITE_URL}/category/${slug}/`,
          },
        ]
      : [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'خانه',
            item: `${process.env.SITE_URL}`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: parent?.title,
            item: `${process.env.SITE_URL}/category/${parent?.slug}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: title,
            item: `${process.env.SITE_URL}/category/${slug}/`,
          },
        ],
  });

  return (
    <>
      <Head>
        <title>{meta_title}</title>
        {/* <script type="application/ld+json">{breadCrumbsScheme}</script> */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: breadCrumbsScheme }}
        />
        <meta property="og:title" content={meta_title} />
        <meta name="description" property="og:description" content={meta_description} />
        {shouldIndexPage ? <meta name="robots" content={robots} /> : <meta name="robots" content="noindex, nofollow" /> }
      </Head>

      {categoryStatus !== 200 ? (
        <ErrorPage statusCode={categoryStatus} />
      ) : (
        <Layout>
          {!isEmpty(children) ? (
            <CategoryPageContent
              slug={slug}
              categoryResponse={categoryResponse}
              subCategoryResponse={subCategoryResponse}
            />
          ) : (
            <SubcategoryPageContent
              slug={slug}
              categoryResponse={categoryResponse}
              categoryPostsResponse={categoryPostsResponse}
            />
          )}
        </Layout>
      )}
    </>
  );
};

export async function getServerSideProps(content) {
  const {
    query: { slug },
    req: { headers: reqHeader },
  } = content;

  const shouldIndexPage = reqHeader ? shouldIndex(reqHeader) : true;

  ///////////////////////////////////////////////////////// get category
  const category = await api.category({
    slug,
    reqHeader,
  });
  const categoryResponse = await category.data;
  const categoryStatus = category.status;

  if (categoryStatus === 404) {
    return {
      notFound: true,
    };
  }

  // Redirect
  const { redirect } = categoryResponse;

  if (!isEmpty(redirect)) {
    return {
      redirect: {
        statusCode: 301,
        destination: redirect,
      },
    };
  }

  ///////////////////////////////////////////////////////// get subcategories
  const subCategory =
    categoryStatus === 200
      ? await Promise.all(
          categoryResponse?.childs?.map(async (row) => {
            return await api.categoryPosts({
              slug: row.slug,
              page: 1,
              reqHeader,
            });
          })
        )
      : [];
  const subCategoryResponse = subCategory.map((row) => row.data);

  ///////////////////////////////////////////////////////// get post of category
  const categoryPosts = await api.categoryPosts({
    slug,
    page: 1,
    reqHeader,
  });
  const categoryPostsResponse = await categoryPosts.data;

  return {
    props: {
      categoryResponse,
      categoryStatus,
      subCategoryResponse,
      categoryPostsResponse,
      shouldIndexPage
    },
  };
}

export default CategoryPage;
